import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRightIcon, EnvelopeIcon } from "@heroicons/react/24/solid";
import me from "../assets/images/me.PNG";
import { FaGithub, FaLinkedin, FaTwitter, FaReact, FaPython, FaPhp, FaLaravel, FaJava, FaJs, FaNodeJs, FaDocker, FaHtml5, FaCss3Alt } from "react-icons/fa";
import { SiTailwindcss, SiMysql, SiSpringboot, SiTensorflow, SiMongodb, SiDjango, SiExpress, SiFirebase, SiStreamlit } from "react-icons/si";
import { useTypewriter, Cursor } from "react-simple-typewriter";

const SOCIALS = [
  {
    name: "github",
    url: "https://github.com/BoubaAhmed",
    icon: <FaGithub aria-label="GitHub" />,
  },
  {
    name: "linkedin",
    url: "https://www.linkedin.com/in/ahmed-bouba-861b91231/",
    icon: <FaLinkedin aria-label="LinkedIn" />,
  },
  {
    name: "twitter",
    url: "https://twitter.com/boubaahmed",
    icon: <FaTwitter aria-label="Twitter" />,
  },
];

const Home = () => {
  const [text] = useTypewriter({
    words: ["AI & Full-Stack Developer", "Machine Learning Enthusiast", "React & Node.js Specialist"],
    loop: true,
    delaySpeed: 1800,
  });

  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => setIsVisible(window.scrollY < 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Tech icons for floating background
  const techIcons = [
    // Existing Icons
    { Icon: FaReact, size: 32, top: '33%', left: '17%', delay: 0, color: '#61DAFB' },
    { Icon: FaPython, size: 32, top: '80%', right: '10%', delay: 1, color: '#3776AB' },
    { Icon: FaLaravel, size: 30, top: '15%', right: '15%', delay: 2, color: '#FF2D20' },
    { Icon: FaJava, size: 34, bottom: '20%', left: '32%', delay: 1.5, color: '#007396' },
    { Icon: FaJs, size: 26, top: '65%', left: '10%', delay: 0.5, color: '#F7DF1E' },
    { Icon: SiTailwindcss, size: 30, bottom: '35%', right: '15%', delay: 2.5, color: '#06B6D4' },
    { Icon: SiMysql, size: 30, top: '26%', right: '22%', delay: 1.8, color: '#4479A1' },
    { Icon: SiSpringboot, size: 30, bottom: '75%', left: '60%', delay: 2.2, color: '#6DB33F' },
    { Icon: FaPhp, size: 30, top: '20%', left: '35%', delay: 0.8, color: '#777BB4' },

    // New Additions for AI/ML & DevOps
    { Icon: SiTensorflow, size: 32, top: '15%', left: '15%', delay: 3, color: '#FF6F00' }, // AI/ML focus
    { Icon: FaNodeJs, size: 30, bottom: '15%', right: '40%', delay: 1.2, color: '#339933' }, // Backend focus
    { Icon: FaDocker, size: 28, top: '40%', right: '8%', delay: 2.7, color: '#2496ED' },   // DevOps focus
    { Icon: SiMongodb, size: 30, bottom: '10%', left: '15%', delay: 1.9, color: '#47A248' },  // Big Data focus

    { Icon: SiDjango, size: 30, top: '80%', left: '45%', delay: 1.4, color: '#092E20' }, // Backend
    { Icon: SiExpress, size: 28, bottom: '45%', left: '12%', delay: 2.1, color: '#000000' }, // Backend (Use white in dark mode)
    { Icon: SiFirebase, size: 28, top: '15%', right: '50%', delay: 3.2, color: '#FFCA28' }, // Tools
    { Icon: FaHtml5, size: 24, top: '45%', right: '25%', delay: 0.7, color: '#E34F26' }, // Web
    { Icon: FaCss3Alt, size: 24, top: '75%', right: '30%', delay: 1.3, color: '#1572B6' }, // Web
    { Icon: SiStreamlit, size: 28, bottom: '5%', right: '25%', delay: 2.8, color: '#FF4B4B' }, // AI/ML Tools
  ];

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
  };

  const stagger = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center bg-white dark:bg-black overflow-hidden"
      id="home"
    >
      {/* Floating background – softer shapes + tech icons */}
      <div className="absolute inset-0 z-0">
        {/* Soft blurred circles */}
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-indigo-400/20 rounded-full mix-blend-soft-light filter blur-3xl animate-float-slow"></div>
        <div className="absolute top-20 right-1/4 w-72 h-72 bg-purple-400/20 rounded-full mix-blend-soft-light filter blur-3xl animate-float animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-pink-400/20 rounded-full mix-blend-soft-light filter blur-3xl animate-float animation-delay-4000"></div>

        {/* Simple cross */}
        <div className="absolute top-1/3 left-1/4 w-12 h-12">
          <div className="absolute w-full h-2 bg-indigo-300/20 rounded-full top-1/2 -translate-y-1/2 rotate-45 blur-sm animate-pulse-slow"></div>
          <div className="absolute w-2 h-full bg-indigo-300/20 rounded-full left-1/2 -translate-x-1/2 rotate-45 blur-sm animate-pulse-slow"></div>
        </div>

        {/* Small dots */}
        <div className="absolute top-10 right-1/3 w-2 h-2 bg-indigo-400/30 rounded-full animate-ping-slow"></div>
        <div className="absolute bottom-20 left-1/4 w-3 h-3 bg-purple-400/30 rounded-full animate-pulse-slow animation-delay-700"></div>

        {/* Floating tech icons – semi‑transparent */}
        {techIcons.map(({ Icon, size, top, left, right, bottom, delay, color }, index) => (
          <div
            key={index}
            className="absolute opacity-40 animate-float-slow"
            style={{
              top: top || 'auto',
              left: left || 'auto',
              right: right || 'auto',
              bottom: bottom || 'auto',
              animationDelay: `${delay}s`,
              color: color, // use brand color with low opacity via CSS (we'll set text color)
            }}
          >
            <Icon size={size} className="text-current" style={{ color: color }} />
          </div>
        ))}
      </div>

      <div className="container mx-auto max-w-4xl relative z-10 px-4 py-12">
        {/* Premium glass card – Apple inspired */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="p-8"
        >
          <div className="flex flex-col md:flex-row items-center gap-10">
            {/* Profile image with refined ring */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative group"
            >
              <div className="relative">
                <div className="w-28 md:w-36 aspect-square rounded-full overflow-hidden ring-4 ring-white/50 dark:ring-gray-800/50 shadow-xl">
                  <img 
                    src={me} 
                    alt="Bouba Ahmed" 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                {/* Subtle glow ring */}
                <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-indigo-500/30 to-purple-500/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
              {/* Small status badge (optional) */}
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white dark:border-black flex items-center justify-center">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              </div>
            </motion.div>

            {/* Text content */}
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="visible"
              className="text-center md:text-left flex-1"
            >
              <motion.h1 variants={fadeInUp} className="text-5xl font-light tracking-tight text-gray-900 dark:text-white mb-3">
                Hey, I'm{" "}
                <span className="font-medium bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 bg-clip-text text-transparent">
                  Bouba Ahmed
                </span>
              </motion.h1>

              <motion.h2 variants={fadeInUp} className="text-2xl md:text-3xl font-light text-gray-600 dark:text-gray-300 mb-4">
                {text}
                <Cursor cursorStyle="▌" cursorColor="#818cf8" />
              </motion.h2>

              <motion.p variants={fadeInUp} className="text-gray-600 dark:text-gray-400 max-w-xl leading-relaxed">
                An <span className="font-medium text-gray-900 dark:text-white">AI & full‑stack developer</span> crafting intelligent applications with Python, React, and modern web technologies.
              </motion.p>
            </motion.div>
          </div>

          {/* Action buttons and social links */}
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="flex flex-col sm:flex-row items-center justify-between gap-6 mt-10"
          >
            <div className="flex flex-wrap justify-center sm:justify-start gap-3">
              <motion.a
                href="#projects"
                variants={fadeInUp}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="group flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg shadow-indigo-600/20 transition-all"
              >
                View Projects
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </motion.a>

              <motion.a
                href="#contact"
                variants={fadeInUp}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="group flex items-center gap-2 px-6 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl border border-gray-200 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-500 transition-all"
              >
                Contact Me
                <EnvelopeIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </motion.a>
            </div>

            <div className="flex items-center gap-5">
              {SOCIALS.map((social, index) => (
                <motion.a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={fadeInUp}
                  whileHover={{ y: -4, scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  aria-label={social.name}
                >
                  {React.cloneElement(social.icon, { className: "w-5 h-5" })}
                </motion.a>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Minimal scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isVisible ? 0.6 : 0 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center z-20 text-gray-700 dark:text-gray-200"
      >
        <span className="text-xs tracking-widest mb-2">SCROLL</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="w-5 h-8 border-2 border-current rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="w-1 h-2 bg-current rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Home;